import React, { useState } from 'react';
import Sidebar, { Tool } from './Sidebar';
import HistoryPanel from '../history/HistoryPanel';
import { Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface MainLayoutProps {
  children: React.ReactNode;
  activeTool: Tool;
  onSelectTool: (tool: Tool) => void;
  user: { id: string; username: string; role: string };
  onLogout: () => Promise<void>;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children, activeTool, onSelectTool, user, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: '#f0f1f7' }}>
      {/* Animated background gradients — brand colors only */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div
          className="absolute top-[-15%] right-[-10%] w-[700px] h-[700px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(84,196,182,0.10) 0%, transparent 70%)' }}
          animate={{ x: [0, 20, 0], y: [0, -15, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-[-20%] left-[15%] w-[600px] h-[600px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(33,105,116,0.08) 0%, transparent 70%)' }}
          animate={{ x: [0, -15, 0], y: [0, 20, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        />
        <motion.div
          className="absolute top-[40%] right-[30%] w-[350px] h-[350px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(228,193,132,0.06) 0%, transparent 70%)' }}
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 6 }}
        />
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.5) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
      </div>

      <Sidebar activeTool={activeTool} onSelectTool={onSelectTool} isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} user={user} onLogout={onLogout} onHistoryOpen={() => setIsHistoryOpen(true)} />
      <HistoryPanel isOpen={isHistoryOpen} onClose={() => setIsHistoryOpen(false)} onSelectTool={(toolId) => { onSelectTool(toolId as any); setIsHistoryOpen(false); }} />

      <div className="flex-1 flex flex-col h-full relative z-10 overflow-hidden">
        {/* Mobile header */}
        <header className="lg:hidden shrink-0 px-4 py-3 flex items-center justify-between"
          style={{ background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.5)' }}>
          <button onClick={() => onSelectTool('home')} className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #54c4b6 0%, #216974 100%)' }}>
              <span className="text-white font-black text-xs leading-none select-none">aN</span>
            </div>
            <span className="font-black text-gray-900 text-sm">
              <span style={{ color: '#54c4b6' }}>aadi</span>
              <span style={{ color: '#e4c184' }}>N</span>
            </span>
          </button>
          <button onClick={() => setIsSidebarOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors">
            <Menu size={20} className="text-gray-600" />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 min-h-full flex flex-col">
            <div className="flex-1">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTool}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>

            <footer className="mt-12 pt-6 pb-4 border-t border-gray-200/50 flex flex-col items-center justify-center space-y-1.5 shrink-0">
              <p className="text-gray-400 text-sm font-bold tracking-wide">powered by Sripada Studios</p>
              <p className="text-gray-500 text-xs font-medium tracking-wide">
                Tool for the beginning of unlimited!
              </p>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
